import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceService } from '../../service.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-detail-leave-request',
  templateUrl: './detail-leave-request.component.html',
  styleUrls: ['./detail-leave-request.component.css']
})
export class DetailLeaveRequestComponent implements OnInit {

  record: any = null;
  loaderShown = true;
  useType = false;

  private pipe = new DatePipe('en-US');

  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ServiceService
  ) {
    this.useType = localStorage.getItem('userTypecon') === 'Admin';
  }

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (!id) { this.router.navigate(['/admin/Leave-request']); return; }
    this.fetchDetail(id);
  }

  fetchDetail(id: string): void {
    this.loaderShown = true;
    this.service.viewLeaveRequestById(id).subscribe({
      next: (res) => {
        this.record = res.data?.[0] || null;
        this.loaderShown = false;
      },
      error: () => {
        this.loaderShown = false;
        this.Toast.fire({ icon: 'error', title: 'Failed to load leave request' });
      }
    });
  }

  approve(): void {
    Swal.fire({
      title: 'Approve Leave Request?',
      text: `Approve leave for ${this.record?.employee_name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#09a109',
      confirmButtonText: 'Yes, Approve',
    }).then(result => {
      if (!result.isConfirmed) return;
      Swal.fire({ title: 'Approving...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      const body = {
        _id: this.record._id,
        status: 'Approved',
        approved_by_id: localStorage.getItem('admin_id') || ''
      };

      this.service.updateLeaveRequest(body).subscribe({
        next: (res) => {
          Swal.close();
          if (res.success) {
            this.Toast.fire({ icon: 'success', title: 'Leave request approved!' });
            this.record.status = 'Approved';
            this.fetchDetail(this.record._id);
          } else {
            this.Toast.fire({ icon: 'error', title: res.message || 'Failed to approve' });
          }
        },
        error: () => { Swal.close(); this.Toast.fire({ icon: 'error', title: 'Error approving request' }); }
      });
    });
  }

  reject(): void {
    Swal.fire({
      title: 'Reject Leave Request',
      input: 'textarea',
      inputLabel: 'Reason for rejection',
      inputPlaceholder: 'Enter rejection reason...',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      confirmButtonColor: '#e53935',
      inputValidator: (v) => !v ? 'Rejection reason is required!' : null
    }).then(result => {
      if (!result.isConfirmed || !result.value) return;
      Swal.fire({ title: 'Rejecting...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      const body = {
        _id: this.record._id,
        status: 'Rejected',
        reject_reason: result.value,
        approved_by_id: localStorage.getItem('admin_id') || ''
      };

      this.service.updateLeaveRequest(body).subscribe({
        next: (res) => {
          Swal.close();
          if (res.success) {
            this.Toast.fire({ icon: 'success', title: 'Leave request rejected.' });
            this.fetchDetail(this.record._id);
          } else {
            this.Toast.fire({ icon: 'error', title: res.message || 'Failed to reject' });
          }
        },
        error: () => { Swal.close(); this.Toast.fire({ icon: 'error', title: 'Error rejecting request' }); }
      });
    });
  }

  preApprove(): void {
    Swal.fire({
      title: 'Pre-Approve Leave Request?',
      text: `Pre-approve leave for ${this.record?.employee_name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#7b5ea7',
      confirmButtonText: 'Yes, Pre-Approve',
    }).then(result => {
      if (!result.isConfirmed) return;
      Swal.fire({ title: 'Processing...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

      const body = {
        _id: this.record._id,
        status: 'Pre approved',
        approved_by_id: localStorage.getItem('admin_id') || ''
      };

      this.service.updateLeaveRequest(body).subscribe({
        next: (res) => {
          Swal.close();
          if (res.success) {
            this.Toast.fire({ icon: 'success', title: 'Leave request pre-approved!' });
            this.fetchDetail(this.record._id);
          } else {
            this.Toast.fire({ icon: 'error', title: res.message || 'Failed to pre-approve' });
          }
        },
        error: () => { Swal.close(); this.Toast.fire({ icon: 'error', title: 'Error pre-approving request' }); }
      });
    });
  }

  goBack(): void {
    this.router.navigate(['/admin/Leave-request-view']);
  }

  DateFormat(value: any): string {
    return this.pipe.transform(value, 'MMM d, y') || '—';
  }

  calcDays(from: string, to: string): string {
    if (!from || !to) return '—';
    const diff = new Date(to).getTime() - new Date(from).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
    return days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '—';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    return name.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase();
  }

  getStatusBadge(status: string): string {
    const map: Record<string, string> = {
      'Pending': 'lr-badge--requested',
      'Pre approved': 'lr-badge--preapproved',
      'Approved': 'lr-badge--approved',
      'Rejected': 'lr-badge--rejected',
    };
    return map[status] || '';
  }

  getStatusIcon(status: string): string {
    const map: Record<string, string> = {
      'Pending': 'fa-clock',
      'Pre approved': 'fa-thumbs-up',
      'Approved': 'fa-check-circle',
      'Rejected': 'fa-times-circle',
    };
    return map[status] || 'fa-circle';
  }

  getLeaveTypeBadge(type: string): string {
    const map: Record<string, string> = {
      'Sick Leave': 'lr-badge--sick-type',
      'Local Leave': 'lr-badge--local-type',
      'Vacation Leave': 'lr-badge--vacation-type',
      'Emergency Leave': 'lr-badge--emergency-type',
      'Official Trip': 'lr-badge--official-type',
      'Compassionate Leave': 'lr-badge--compassionate-type',
    };
    return map[type] || '';
  }
}